import React from 'react';
import { Eye, PenTool } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/common/utils';
import { useProtectedStorageUrl } from './useProtectedStorageUrl';

interface SignatureThumbnailCardProps {
  signatureUrl?: string;
  signerName: string;
  onClick: () => void;
}

export const SignatureThumbnailCard: React.FC<SignatureThumbnailCardProps> = ({
  signatureUrl,
  signerName,
  onClick,
}) => {
  const { objectUrl, isLoading, hasError } = useProtectedStorageUrl(signatureUrl);

  if (!signatureUrl) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-28 overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition hover:border-gray-300 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2'
      )}
      aria-label={`Ver firma de ${signerName}`}
      title={`Ver firma de ${signerName}`}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-2 py-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Firma
        </span>
        <Eye className="h-3.5 w-3.5 text-gray-400 transition group-hover:text-gray-700" />
      </div>

      <div className="flex h-20 items-center justify-center bg-gray-50">
        {isLoading && <Skeleton className="h-full w-full rounded-none" />}

        {!isLoading && objectUrl && (
          <img
            src={objectUrl}
            alt={`Miniatura de la firma de ${signerName}`}
            className="h-full w-full object-contain p-2"
          />
        )}

        {!isLoading && !objectUrl && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
            <PenTool className={cn('h-5 w-5', hasError ? 'text-red-400' : 'text-gray-400')} />
            <span className={cn('text-[11px]', hasError ? 'text-red-500' : 'text-gray-500')}>
              {hasError ? 'Error' : 'Sin firma'}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};
